import classnames from 'classnames';
import { Table, notification } from 'antd';
import OverflowMenu from '@components/shared/OverflowMenu';
import OverflowMenuItem from '@components/shared/OverflowMenuItem';
import CustomIcon from '@components/shared/CustomIcon';
import api from '@shared/api';
import useToggle from '@shared/useToggle';
import routes from '@root/routes';
import { format, parseISO } from 'date-fns';
import addKeyTo from '@shared/addKeyTo';
import { useTranslation } from 'react-i18next';
import { AdminFloorPlan } from '@root/types';
import Typography from '@styles/typography.module.less';
import FloorPlanUploader from './FloorPlanUploader';
import s from './FloorPlanTable.module.less';

type Props = {
  floorPlan: AdminFloorPlan | null;
  fetchListing: () => void;
  listingId: string;
};

const FloorPlanTable = ({ floorPlan, fetchListing, listingId }: Props) => {
  const { t } = useTranslation('admin');
  const { value: isDeleting, setTrue: startDeleting, setFalse: stopDeleting } = useToggle();

  const tableData = () => (floorPlan ? addKeyTo([floorPlan]) : []);

  const deleteFloorPlanButtonHandler = async (id: string) => {
    // eslint-disable-next-line no-alert
    if (window.confirm(t('media.deleteConfirmation'))) {
      startDeleting();
      const resp = await api.delete(routes.api.admin.floorPlan(id));

      if (resp.ok) {
        fetchListing();
        notification.success({ message: t('media.floorPlan.deleteSuccess') });
      } else {
        notification.error({ message: t('media.floorPlan.deleteFailure') });
      }
      stopDeleting();
    }
  };

  const renderFile = (path: string, filename: string, processedAt: string) => {
    return (
      <div className={s.fileContainer}>
        <div className={s.fileInfoContainer}>
          {processedAt ? (
            <span className={s.processedAt}>
              {t('media.processedAt', {
                date: format(parseISO(processedAt), 'PP'),
                time: format(parseISO(processedAt), 'p'),
              })}
            </span>
          ) : null}
        </div>
        <div className={s.thumbnailContainer}>
          <div className={s.thumbnail}>
            <img src={path} alt="Floor Plan" />
          </div>
        </div>
      </div>
    );
  };

  const columns = () => [
    {
      width: '91%',
      render: row => {
        return renderFile(row.path, row.filename, row.processedAt);
      },
    },
    {
      width: '9%',
      render: row => (
        <OverflowMenu overflowClassName={s.overflowMenu}>
          <OverflowMenuItem
            className={s.overflowItem}
            disabled={isDeleting}
            onClick={() => deleteFloorPlanButtonHandler(row.id)}
          >
            <>
              <CustomIcon type="trash"></CustomIcon>
              {t('admin:listing.mediaManagement.delete')}
            </>
          </OverflowMenuItem>
        </OverflowMenu>
      ),
    },
  ];

  return (
    <>
      <div className={s.header}>
        <div className={classnames(Typography.headerMediumSemibold, s.title)}>
          {t('media.floorPlan.title')}
        </div>
        <div className={s.uploadButton}>
          <FloorPlanUploader
            fetchListing={fetchListing}
            listingId={listingId}
            buttonClass={s.uploadButtonClass}
            buttonIcon="upload"
            buttonSize="small"
          />
        </div>
        {floorPlan ? (
          <span className={s.uploadHelpText}>{t('media.floorPlan.helpText')}</span>
        ) : null}
      </div>
      <Table
        className={s.tableHeader}
        rowClassName={s.row}
        pagination={false}
        columns={columns()}
        dataSource={tableData()}
      />
    </>
  );
};

export default FloorPlanTable;
